'use client';

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { CalendarIcon } from '@radix-ui/react-icons';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Calendar, localeMapping } from '@eliadmin/core/src/components/ui/calendar';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Switch } from '@eliadmin/core/src/components/ui/switch';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import {
  createVehicle,
  updateVehicle,
  type FuelType,
  type Vehicle,
  type VehiclePayload,
} from '../../api-custom/vehicles-api';

const FUEL_TYPE_OPTIONS: { value: FuelType; label: string }[] = [
  { value: 'petrol', label: 'Benzín' },
  { value: 'diesel', label: 'Nafta' },
  { value: 'electric', label: 'Elektrina' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'lpg', label: 'LPG' },
];

const EMPTY_PAYLOAD: VehiclePayload = {
  vehicle_plate: '',
  vehicle_type: '',
  vin_number: '',
  engine_volume: '',
  total_weight: '',
  engine_power: '',
  fuel_type: 'diesel',
  registration_date: '',
  insurer_name: '',
  policy_number: '',
  green_card_number: '',
  green_card_valid_until: '',
  vehicle_insurance: true,
  insurance_company_name: '',
  is_active: true,
};

function toPayload(vehicle: Vehicle): VehiclePayload {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...payload } = vehicle;
  return payload;
}

type DateFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
};

function DateField({ label, value, onChange }: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const localeCode = useLocaleStore(s => s.currentLocale);
  const dateLocale = localeMapping[localeCode] || enUS;
  const selected = value ? new Date(value) : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start rounded-[6px] text-left font-normal"
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-primary" />
            <span className="truncate">
              {selected ? format(selected, 'PPP', { locale: dateLocale }) : 'Vyberte dátum'}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent side="bottom" align="start" className="w-auto rounded-[6px] p-0 shadow-lg">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={date => {
              if (date) onChange(format(date, 'yyyy-MM-dd'));
              setOpen(false);
            }}
            initialFocus
            fromYear={1970}
            toYear={new Date().getFullYear() + 5}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

type VehicleFormProps = {
  vehicle?: Vehicle;
  onSuccess: () => void;
};

export function VehicleForm({ vehicle, onSuccess }: VehicleFormProps) {
  const { t } = useTranslations();
  const authToken = useCustomerStore(s => s.me!.authToken);
  const locale = useLocaleStore(s => s.currentLocale);
  const queryClient = useQueryClient();
  const isEditing = !!vehicle;

  const [payload, setPayload] = useState<VehiclePayload>(
    vehicle ? toPayload(vehicle) : EMPTY_PAYLOAD,
  );
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  function setField<K extends keyof VehiclePayload>(key: K, value: VehiclePayload[K]) {
    setPayload(prev => ({ ...prev, [key]: value }));
  }

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      isEditing
        ? updateVehicle({ locale, authToken, id: vehicle.id, payload })
        : createVehicle({ locale, authToken, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast({
        title: isEditing ? 'Vozidlo bolo upravené.' : 'Vozidlo bolo pridané.',
        variant: 'success',
      });
      onSuccess();
    },
    onError: e => {
      if (e instanceof ValidationError) {
        setErrors(e.fields);
        toast({ title: 'Skontrolujte prosím vyplnené údaje.', variant: 'destructive' });
      } else {
        toast({
          title: 'Uloženie vozidla zlyhalo. Skúste to prosím znova.',
          variant: 'destructive',
        });
      }
    },
  });

  return (
    <form
      className="flex w-full flex-col gap-5"
      onSubmit={e => {
        e.preventDefault();
        mutate();
      }}
    >
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>{t('text4079')}</Label>
          <Input
            variant="outlined"
            className={{ wrapper: 'rounded-[6px]' }}
            value={payload.vehicle_plate}
            onChange={e => setField('vehicle_plate', e.target.value)}
            required
          />
          {errors.vehicle_plate && (
            <p className="text-xs text-destructive-foreground">{errors.vehicle_plate[0]}</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Značka a model</Label>
          <Input
            variant="outlined"
            className={{ wrapper: 'rounded-[6px]' }}
            value={payload.vehicle_type}
            onChange={e => setField('vehicle_type', e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>VIN</Label>
          <Input
            variant="outlined"
            className={{ wrapper: 'rounded-[6px]' }}
            value={payload.vin_number}
            onChange={e => setField('vin_number', e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Palivo</Label>
          <Select
            value={payload.fuel_type}
            onValueChange={value => setField('fuel_type', value as FuelType)}
          >
            <SelectTrigger variant="outlined" className="w-full rounded-[6px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FUEL_TYPE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Objem motora (cm³)</Label>
          <Input
            type="number"
            variant="outlined"
            className={{ wrapper: 'rounded-[6px]' }}
            value={payload.engine_volume}
            onChange={e => setField('engine_volume', e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Výkon motora (kW)</Label>
          <Input
            type="number"
            variant="outlined"
            className={{ wrapper: 'rounded-[6px]' }}
            value={payload.engine_power}
            onChange={e => setField('engine_power', e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Celková hmotnosť (kg)</Label>
          <Input
            type="number"
            variant="outlined"
            className={{ wrapper: 'rounded-[6px]' }}
            value={payload.total_weight}
            onChange={e => setField('total_weight', e.target.value)}
          />
        </div>

        <DateField
          label="Dátum evidencie"
          value={payload.registration_date}
          onChange={value => setField('registration_date', value)}
        />

        {/* Názov poisťovne je uložený v `insurer_name` – pole `insurance_company_name`
            zostáva v payloade, ale nezobrazuje sa. */}
        <div className="flex flex-col gap-1.5">
          <Label>Poisťovacia spoločnosť</Label>
          <Input
            variant="outlined"
            className={{ wrapper: 'rounded-[6px]' }}
            value={payload.insurer_name}
            onChange={e => setField('insurer_name', e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Číslo poistky</Label>
          <Input
            variant="outlined"
            className={{ wrapper: 'rounded-[6px]' }}
            value={payload.policy_number}
            onChange={e => setField('policy_number', e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Číslo zelenej karty</Label>
          <Input
            variant="outlined"
            className={{ wrapper: 'rounded-[6px]' }}
            value={payload.green_card_number}
            onChange={e => setField('green_card_number', e.target.value)}
          />
        </div>

        <DateField
          label="Platnosť zelenej karty do"
          value={payload.green_card_valid_until}
          onChange={value => setField('green_card_valid_until', value)}
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
        <label className="flex items-center gap-3">
          <Switch
            checked={payload.vehicle_insurance}
            onCheckedChange={checked => setField('vehicle_insurance', checked)}
          />
          <span className="text-sm">Vozidlo je poistené</span>
        </label>
        <label className="flex items-center gap-3">
          <Switch
            checked={payload.is_active}
            onCheckedChange={checked => setField('is_active', checked)}
          />
          <span className="text-sm">Aktívne vozidlo</span>
        </label>
      </div>

      <Button type="submit" isLoading={isPending} className="w-full rounded-[6px] sm:w-fit">
        {isEditing ? 'Uložiť zmeny' : t('text4058')}
      </Button>
    </form>
  );
}
